import React, { useEffect, useState } from 'react';
import { Dimmer, Loader, Modal, Icon } from 'semantic-ui-react';
import { Registrant } from '../../types/Registrant';

interface Props {
    open: boolean;
    loading?: boolean;
    onClose: () => void;
    resident: Registrant | null;
}

const ResidentDetailModal = ({ open, loading, onClose, resident }: Props) => {
    const fullName = resident
        ? ((resident.FirstName || '') + ' ' + (resident.LastName || '')).trim()
        : '';
    const phone = resident ? (resident.Cell || resident.Phone || '') : '';

    // Either the stored photo or the placeholder asset can fail to load; fall back
    // to the drawn person icon rather than leaving a broken image in the header.
    const [photoFailed, setPhotoFailed] = useState(false);
    useEffect(() => {
        setPhotoFailed(false);
    }, [resident?._id]);

    // "musicListening" -> "Music Listening"
    const formatInterest = (key: string) => {
        const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
        return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    };

    // Interests come back as { Category: { subCategory: ['item', ...] } }. Chips are
    // grouped under the top-level category so long lists stay scannable.
    const getInterestGroups = (): { label: string; items: string[] }[] => {
        if (!resident) return [];

        if (resident.ResidentInterests) {
            const collect = (node: any, into: string[]) => {
                if (Array.isArray(node)) {
                    node.forEach(item => collect(item, into));
                } else if (typeof node === 'object' && node !== null) {
                    Object.values(node).forEach(value => collect(value, into));
                } else if (typeof node === 'string' && node.trim()) {
                    into.push(node.trim());
                }
            };
            const groups = Object.entries(resident.ResidentInterests as Record<string, any>)
                .map(([label, value]) => {
                    const items: string[] = [];
                    collect(value, items);
                    // Dedupe on a case-insensitive key, so "tv" and "TV" collapse to one
                    // chip instead of rendering twice under a duplicate React key.
                    // formatInterest alone doesn't normalize case (it only spaces
                    // camelCase boundaries), so Set-on-formatted-value isn't enough.
                    const byKey = new Map<string, string>();
                    items.forEach(item => {
                        const key = item.toLowerCase();
                        if (!byKey.has(key)) byKey.set(key, formatInterest(item));
                    });
                    return {
                        label: formatInterest(label),
                        items: Array.from(byKey.values()),
                    };
                })
                .filter(group => group.items.length > 0);
            if (groups.length > 0) return groups;
        }

        // Older payloads only carry a flat comma-separated string; render it ungrouped.
        if (resident.ResidentInterestsString) {
            const items = resident.ResidentInterestsString
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);
            if (items.length > 0) return [{ label: '', items }];
        }

        return [];
    };

    const interestGroups = getInterestGroups();

    const aboutText = resident?.SpecialHandling && resident.SpecialHandling !== '-'
        ? resident.SpecialHandling
        : '';

    return (
        <Modal size="small" centered open={open} onClose={onClose} className="resident-detail-modal">
            <Modal.Content>
                {loading ? (
                    <div style={{ minHeight: 200, position: 'relative' }}>
                        <Dimmer active inverted>
                            <Loader>Loading...</Loader>
                        </Dimmer>
                    </div>
                ) : resident ? (
                    <div className="resident-detail-content">
                        <button
                            type="button"
                            className="resident-detail-close"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <Icon name="close" />
                        </button>

                        <div className="resident-detail-header">
                            <div className="resident-detail-photo">
                                {photoFailed ? (
                                    <Icon name="user outline" aria-hidden="true" />
                                ) : (
                                    <img
                                        src={resident.Image || '/avatar_placeholder.png'}
                                        alt={fullName}
                                        onError={() => setPhotoFailed(true)}
                                    />
                                )}
                            </div>
                            <div className="resident-detail-name-block">
                                <div className="resident-detail-name">{fullName}</div>
                                {resident.UnitName && (
                                    <div className="resident-detail-room">
                                        <Icon name="home" />
                                        <span>Room {resident.UnitName}</span>
                                    </div>
                                )}
                                {(resident.Email || phone) && (
                                    <div className="resident-detail-contact">
                                        {resident.Email && (
                                            <div className="resident-detail-contact-line">
                                                <Icon name="mail outline" />
                                                <a href={`mailto:${resident.Email}`} target="_blank" rel="noopener noreferrer">
                                                    {resident.Email}
                                                </a>
                                            </div>
                                        )}
                                        {phone && (
                                            <div className="resident-detail-contact-line">
                                                <Icon name="phone" />
                                                <a href={`tel:${phone}`}>{phone}</a>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="resident-detail-body">
                            {aboutText && <div className="resident-detail-about">{aboutText}</div>}

                            <div className="resident-detail-interests-title">Interests</div>
                            {interestGroups.length > 0 ? (
                                interestGroups.map(group => (
                                    <div className="resident-detail-interest-group" key={group.label || 'all'}>
                                        {group.label && (
                                            <div className="resident-detail-interest-label">
                                                <Icon name="heart outline" />
                                                {group.label}
                                            </div>
                                        )}
                                        <div className="resident-detail-chip-row">
                                            {group.items.map(item => (
                                                <span key={item} className="resident-detail-interest-tag">{item}</span>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <span className="resident-detail-interests-empty">No interests added yet</span>
                            )}
                        </div>
                    </div>
                ) : null}
            </Modal.Content>
        </Modal>
    );
};

export default ResidentDetailModal;
